import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createApi } from './api'
import {
  DEFAULT_POSITION, DEFAULT_TABLE, boxKey, boxOf, boxesOf, defaultSize, isSection, parseBoxKey, positionable, withBox
} from './fieldTypes'
import { TranslationsContext } from './translations'
import TopBar from './TopBar'
import StructurePanel from './StructurePanel'
import PdfCanvas from './PdfCanvas'
import FormPreview from './FormPreview'
import Inspector from './Inspector'

const SAVE_DELAY = 500

const sameField = (one, other) => one === other || JSON.stringify(one) === JSON.stringify(other)

const FormBuilder = ({ initialState, translations, meta }) => {
  const api = useMemo(() => createApi(initialState.blueprint.id), [initialState.blueprint.id])

  const [blueprint, setBlueprint] = useState(initialState.blueprint)
  const [fields, setFields]       = useState(initialState.fields)
  const [pages]                   = useState(initialState.pages)
  const [permissions]             = useState(initialState.permissions)
  const [selection, setSelection] = useState([])
  const [view, setView]           = useState(initialState.pages.length > 0 ? 'pdf' : 'form')
  const [saving, setSaving]       = useState({ pending: 0, error: null, savedAt: null })
  const [styleClipboard, setStyleClipboard] = useState(null)
  const [activeBox, setActiveBox] = useState(null)
  const [placing, setPlacing]     = useState(null)

  const fieldsRef    = useRef(fields)
  const blueprintRef = useRef(blueprint)
  fieldsRef.current    = fields
  blueprintRef.current = blueprint

  const timers   = useRef({})
  const chains   = useRef({})
  const versions = useRef({})

  const readOnly = blueprint.yuman_form
  const hasPdf   = pages.length > 0

  const track = useCallback(async (promise) => {
    setSaving((state) => ({ ...state, pending: state.pending + 1, error: null }))
    try {
      const result = await promise
      setSaving((state) => ({ ...state, pending: state.pending - 1, savedAt: new Date() }))
      return result
    } catch (error) {
      setSaving((state) => ({ ...state, pending: state.pending - 1, error: (error.messages || [error.message]).join(', ') }))
      throw error
    }
  }, [])

  // Keeps what is still waiting to be saved, takes everything else from the server
  // An unchanged field keeps its object, so the memoised rows and boxes skip it
  const applyServerState = useCallback((state) => {
    if (!state || !state.fields) { return }

    setFields((current) => {
      const local = Object.fromEntries(current.map((field) => [field.id, field]))
      const mergeOne = (serverField) => {
        const localField = local[serverField.id]
        if (!localField) { return serverField }

        let merged = versions.current[`field-${serverField.id}`] ? { ...localField, order: serverField.order } : serverField
        boxesOf(localField).concat(boxesOf(serverField)).forEach(({ key, option }) => {
          if (versions.current[`position-${key}`]) { merged = withBox(merged, option, boxOf(localField, option)) }
        })
        return sameField(merged, localField) ? localField : merged
      }

      if (state.partial) {
        const updates = Object.fromEntries(state.fields.map((field) => [field.id, mergeOne(field)]))
        return current.map((field) => updates[field.id] || field)
      }
      return state.fields.map(mergeOne)
    })
    if (state.blueprint) {
      setBlueprint((current) => (versions.current.form ? current : state.blueprint))
    }
  }, [])

  const schedule = useCallback((key, save, delay = SAVE_DELAY) => {
    versions.current[key] = (versions.current[key] || 0) + 1
    clearTimeout(timers.current[key])
    timers.current[key] = setTimeout(() => {
      const version = versions.current[key]
      chains.current[key] = (chains.current[key] || Promise.resolve())
        .then(() => track(save()))
        .then((result) => {
          if (versions.current[key] === version) { delete versions.current[key] }
          return result
        })
        .catch(() => { delete versions.current[key] })
    }, delay)
  }, [track])

  const flushBeforeLeaving = useCallback((event) => {
    if (Object.keys(versions.current).length === 0) { return }
    event.preventDefault()
    event.returnValue = ''
  }, [])

  useEffect(() => {
    window.addEventListener('beforeunload', flushBeforeLeaving)
    return () => window.removeEventListener('beforeunload', flushBeforeLeaving)
  }, [flushBeforeLeaving])

  const updateForm = useCallback((changes) => {
    setBlueprint((current) => ({ ...current, ...changes }))
    schedule('form', async () => {
      const { name, display_name, can_be_filled, attachable_to_quote, attachable_to_workorder,
              attachable_to_purchase_order, text_align, label_width, submit_url } = blueprintRef.current
      const attributes = { name, display_name, can_be_filled, attachable_to_quote, attachable_to_workorder,
                           attachable_to_purchase_order, text_align, label_width }
      if (permissions.edit_submit_url) { attributes.submit_url = submit_url }
      return api.updateForm(attributes)
    })
  }, [api, permissions, schedule])

  const findField = (id) => fieldsRef.current.find((field) => field.id === id)

  const updateField = useCallback((id, changes) => {
    setFields((current) => current.map((field) => (field.id === id ? { ...field, ...changes } : field)))
    schedule(`field-${id}`, async () => {
      const field  = findField(id)
      const result = isSection(field) ? await api.updateSection(field) : await api.updateField(field)
      applyServerState(result)
      return result
    })
  }, [api, applyServerState, schedule])

  const createField = useCallback(async ({ dataType = 'text', sectionId = null, order, name, position, extra = {} }) => {
    const siblings = fieldsRef.current.filter((field) => !isSection(field) && field.section_id === sectionId)
    const field = {
      field_type:    'data',
      can_be_copied: true,
      text_align:    'left',
      ...extra,
      data_type:     dataType,
      name:          name || translations.types[dataType],
      section_id:    sectionId,
      order:         order || (siblings.length + 1) * 10 + 1000
    }
    if (dataType === 'table' && !field.table_columns) {
      field.table_columns = { ...DEFAULT_TABLE, columns: [{ ...DEFAULT_TABLE.columns[0], name: translations.column_default }] }
    }
    const result = await track(api.createField(field))
    applyServerState(result)
    if (result.created_id) {
      setSelection([result.created_id])
      if (position) {
        const created = await track(api.createPosition({ ...DEFAULT_POSITION, ...position, field_id: result.created_id }))
        setFields((current) => current.map((item) => (item.id === result.created_id ? { ...item, position: created } : item)))
      }
    }
    return result.created_id
  }, [api, applyServerState, track, translations])

  const createSection = useCallback(async () => {
    const sections = fieldsRef.current.filter((field) => !field.section_id)
    const result = await track(api.createSection({ name: translations.new_section, order: sections.length + 1000, duplicable: false, text_align: 'left' }))
    applyServerState(result)
    if (result.created_id) { setSelection([result.created_id]) }
  }, [api, applyServerState, track, translations])

  const duplicateField = useCallback(async (id) => {
    const original = findField(id)
    if (!original) { return }

    if (isSection(original)) {
      const result = await track(api.duplicateSection(id))
      applyServerState(result)
      if (result.created_id) { setSelection([result.created_id]) }
      return
    }

    const { id: _id, position: _position, option_positions: _optionPositions, ...attributes } = original
    const copyId = await createField({
      dataType:  original.data_type,
      sectionId: original.section_id,
      order:     original.order,
      name:      `${original.name} (${translations.copy})`,
      extra:     attributes
    })
    if (copyId) {
      const boxes = boxesOf(original)
      const lowest = Math.max(...boxes.map((box) => box.position.y2), 0)
      const highest = Math.min(...boxes.map((box) => box.position.y1), lowest)
      const offset = lowest - highest + 4
      for (const box of boxes) {
        const { id: _positionId, ...style } = box.position
        const created = await track(api.createPosition({ ...style, field_id: copyId, option: box.option, y1: style.y1 + offset, y2: style.y2 + offset }))
        setFields((current) => current.map((item) => (item.id === copyId ? withBox(item, box.option, created) : item)))
      }
    }
  }, [api, applyServerState, createField, track, translations])

  const deleteField = useCallback(async (id) => {
    const field = findField(id)
    if (!field) { return }

    const message = isSection(field) ? translations.confirm_delete_section : translations.confirm_delete_field
    if (!window.confirm(message)) { return }

    clearTimeout(timers.current[`field-${id}`])
    delete versions.current[`field-${id}`]
    setSelection((current) => current.filter((selected) => selected !== id))
    const result = await track(isSection(field) ? api.destroySection(id) : api.destroyField(id))
    applyServerState(result)
  }, [api, applyServerState, track, translations])

  const reorder = useCallback(async (items) => {
    setFields((current) => {
      const byId = Object.fromEntries(items.map((item) => [item.id, item]))
      return current.map((field) => (byId[field.id] ? { ...field, section_id: byId[field.id].section_id, order: byId[field.id].order } : field))
    })
    try {
      const result = await track(api.reorder(items))
      applyServerState(result)
    } catch (_error) {
      applyServerState(await api.load())
    }
  }, [api, applyServerState, track])

  const savePosition = useCallback((key) => {
    const { fieldId, option } = parseBoxKey(key)
    schedule(`position-${key}`, async () => {
      const field = findField(fieldId)
      const position = field && boxOf(field, option)
      if (!position) { return null }

      const saved = position.id
        ? await api.updatePosition(position)
        : await api.createPosition({ ...position, field_id: fieldId, option })
      setFields((current) => current.map((item) => {
        const box = item.id === fieldId && boxOf(item, option)
        return box ? withBox(item, option, { ...box, id: saved.id }) : item
      }))
      return saved
    }, 350)
  }, [api, schedule])

  const updatePositions = useCallback((changes) => {
    const byField = {}
    Object.entries(changes).forEach(([key, change]) => {
      const { fieldId, option } = parseBoxKey(key)
      ;(byField[fieldId] = byField[fieldId] || []).push([option, change])
    })
    setFields((current) => current.map((field) => {
      if (!byField[field.id]) { return field }
      return byField[field.id].reduce((result, [option, change]) => withBox(result, option, {
        ...DEFAULT_POSITION, ...(boxOf(result, option) || {}), ...change, field_id: field.id, option
      }), field)
    }))
    Object.keys(changes).forEach((key) => savePosition(key))
  }, [savePosition])

  const placeField = useCallback((fieldId, page, x, y, option = null) => {
    const field = findField(fieldId)
    if (!field || !positionable(field)) { return }

    const { w, h } = option === null ? defaultSize(field) : { w: 12, h: 12 }
    const style = styleClipboard || {}
    updatePositions({ [boxKey(fieldId, option)]: { ...style, page_number: page, x1: x, y1: y, x2: x + w, y2: y + h } })
    setSelection([fieldId])
    setActiveBox(boxKey(fieldId, option))
    setPlacing(null)
  }, [styleClipboard, updatePositions])

  const removePositions = useCallback((keys) => {
    keys.forEach((key) => {
      const { fieldId, option } = parseBoxKey(key)
      const field = findField(fieldId)
      const position = field && boxOf(field, option)
      if (!position) { return }

      clearTimeout(timers.current[`position-${key}`])
      delete versions.current[`position-${key}`]
      setFields((current) => current.map((item) => (item.id === fieldId ? withBox(item, option, null) : item)))
      const chainKey = `position-${key}`
      chains.current[chainKey] = (chains.current[chainKey] || Promise.resolve()).then(() => {
        const id = position.id || boxOf(findField(fieldId) || {}, option)?.id
        return id ? track(api.destroyPosition(id)).catch(() => null) : null
      })
    })
  }, [api, track])

  const fieldBoxKeys = (fieldIds) => fieldsRef.current
    .filter((field) => fieldIds.includes(field.id))
    .flatMap((field) => boxesOf(field).map((box) => box.key))

  useEffect(() => {
    if (activeBox && !selection.includes(parseBoxKey(activeBox).fieldId)) { setActiveBox(null) }
    if (placing && !selection.includes(placing.fieldId)) { setPlacing(null) }
  }, [selection])

  const selectedFields = fields.filter((field) => selection.includes(field.id))

  // Stable, or every memoised row and box redraws
  const actions = useMemo(() => ({
    updateForm, updateField, createField, createSection, duplicateField, deleteField, reorder,
    updatePositions, placeField, removePositions, setSelection, setStyleClipboard, setActiveBox, setPlacing, fieldBoxKeys
  }), [
    updateForm, updateField, createField, createSection, duplicateField, deleteField, reorder,
    updatePositions, placeField, removePositions, setStyleClipboard
  ])

  return (
    <TranslationsContext.Provider value={translations}>
      <div className="fb">
        <TopBar
          blueprint={blueprint}
          saving={saving}
          meta={meta}
          hasPdf={hasPdf}
          view={view}
          setView={setView}
          onSettings={() => setSelection([])}
        />
        <div className="fb-body">
          <StructurePanel
            fields={fields}
            selection={selection}
            hasPdf={hasPdf}
            readOnly={readOnly}
            actions={actions}
          />
          <main className="fb-stage">
            {view === 'pdf' && hasPdf
              ? <PdfCanvas
                  pages={pages}
                  fields={fields}
                  selection={selection}
                  readOnly={readOnly}
                  styleClipboard={styleClipboard}
                  activeBox={activeBox}
                  placing={placing}
                  actions={actions}
                />
              : <FormPreview
                  blueprint={blueprint}
                  fields={fields}
                  selection={selection}
                  actions={actions}
                />
            }
          </main>
          <Inspector
            blueprint={blueprint}
            fields={fields}
            selectedFields={selectedFields}
            pages={pages}
            permissions={permissions}
            readOnly={readOnly}
            meta={meta}
            styleClipboard={styleClipboard}
            activeBox={activeBox}
            placing={placing}
            actions={actions}
          />
        </div>
      </div>
    </TranslationsContext.Provider>
  )
}

export default FormBuilder
